import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTournamentDetailsTemplateAPI,
  deleteTournamentDetailsTemplateAPI,
  fetchTournamentDetailsTemplatesAPI,
  updateTournamentDetailsTemplateAPI,
} from "./tournamentDetailsTemplatesAPI";
import { showModal } from "../statusModal/statusModal";

const initialState = {
  list: [],
  // Full option set for the tournament create/edit picker (kept separate from
  // the paginated `list` so the dropdown isn't limited to one page).
  options: [],
  fetchStatus: "idle",
  optionsFetchStatus: "idle",
  addStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
  currentPage: 1,
  totalPages: 1,
  pageLimit: 10,
  error: null,
};

// Paginated list. No success toast on fetch — the list refreshes on mount and
// on infinite scroll, so a toast per page would be noise (mutations still toast).
export const fetchTournamentDetailsTemplates = createAsyncThunk(
  "tournamentDetailsTemplates/fetch",
  async ({ page } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const { tournamentDetailsTemplates } = getState();
      const limit = tournamentDetailsTemplates.pageLimit;
      const data = await fetchTournamentDetailsTemplatesAPI(page, limit);
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب القوالب", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch templates",
      );
    }
  },
);

// Options for the tournament create/edit picker — one large page (backend caps
// limit at 100) rather than the paginated list, so the dropdown has every
// template. Refetched whenever templates are mutated (see reducers below).
export const fetchTournamentDetailsTemplateOptions = createAsyncThunk(
  "tournamentDetailsTemplates/fetchOptions",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchTournamentDetailsTemplatesAPI(1, 100);
      return data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء جلب القوالب", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch template options",
      );
    }
  },
);

export const createTournamentDetailsTemplate = createAsyncThunk(
  "tournamentDetailsTemplates/create",
  async (templateData, { dispatch, rejectWithValue }) => {
    try {
      const data = await createTournamentDetailsTemplateAPI(templateData);
      dispatch(
        showModal({ message: "تم انشاء القالب بنجاح", status: "success" }),
      );
      return data.data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء انشاء القالب", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to create template",
      );
    }
  },
);

export const updateTournamentDetailsTemplate = createAsyncThunk(
  "tournamentDetailsTemplates/update",
  async ({ templateId, templateData }, { dispatch, rejectWithValue }) => {
    try {
      const data = await updateTournamentDetailsTemplateAPI(
        templateId,
        templateData,
      );
      dispatch(
        showModal({ message: "تم تعديل القالب بنجاح", status: "success" }),
      );
      return data.data;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء تعديل القالب", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to update template",
      );
    }
  },
);

export const deleteTournamentDetailsTemplate = createAsyncThunk(
  "tournamentDetailsTemplates/delete",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteTournamentDetailsTemplateAPI(id);
      dispatch(showModal({ message: "تم حذف القالب بنجاح", status: "success" }));
      return data.data._id;
    } catch (error) {
      dispatch(
        showModal({ message: "حصل خطأ اثناء حذف القالب", status: "error" }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete template",
      );
    }
  },
);

const tournamentDetailsTemplatesSlice = createSlice({
  name: "tournamentDetailsTemplates",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTournamentDetailsTemplates.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournamentDetailsTemplates.fulfilled, (state, action) => {
        const pagination = action.payload?.data?.pagination || {};
        const page = Number(pagination.page || 1);
        const responseList = Array.isArray(action.payload?.data?.items)
          ? action.payload.data.items
          : [];

        // Page 1 replaces (fresh/refresh); later pages append, de-duped by _id.
        const newData =
          page === 1
            ? responseList
            : responseList.filter(
                (item) =>
                  !state.list.find((existing) => existing._id === item._id),
              );

        state.fetchStatus = "succeeded";
        state.list = page === 1 ? newData : [...state.list, ...newData];
        state.currentPage = page;
        state.totalPages = pagination.totalPages || 1;
      })
      .addCase(fetchTournamentDetailsTemplates.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload;
      })
      .addCase(fetchTournamentDetailsTemplateOptions.pending, (state) => {
        state.optionsFetchStatus = "loading";
      })
      .addCase(
        fetchTournamentDetailsTemplateOptions.fulfilled,
        (state, action) => {
          state.optionsFetchStatus = "succeeded";
          state.options = Array.isArray(action.payload?.data?.items)
            ? action.payload.data.items
            : [];
        },
      )
      .addCase(fetchTournamentDetailsTemplateOptions.rejected, (state) => {
        state.optionsFetchStatus = "failed";
      })
      .addCase(createTournamentDetailsTemplate.pending, (state) => {
        state.addStatus = "loading";
        state.error = null;
      })
      .addCase(createTournamentDetailsTemplate.fulfilled, (state, action) => {
        state.addStatus = "succeeded";
        // Newest-first list → put the new template at the top.
        state.list.unshift(action.payload);
        // Picker options are now stale — force a refresh on next form mount.
        state.optionsFetchStatus = "idle";
      })
      .addCase(createTournamentDetailsTemplate.rejected, (state, action) => {
        state.addStatus = "failed";
        state.error = action.payload;
      })
      .addCase(updateTournamentDetailsTemplate.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateTournamentDetailsTemplate.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.list = state.list.map((template) =>
          template._id === action.payload._id ? action.payload : template,
        );
        state.optionsFetchStatus = "idle";
      })
      .addCase(updateTournamentDetailsTemplate.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload;
      })
      .addCase(deleteTournamentDetailsTemplate.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteTournamentDetailsTemplate.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.list = state.list.filter(({ _id }) => _id !== action.payload);
        state.options = state.options.filter(
          ({ _id }) => _id !== action.payload,
        );
        state.optionsFetchStatus = "idle";
      })
      .addCase(deleteTournamentDetailsTemplate.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload;
      });
  },
});

export default tournamentDetailsTemplatesSlice.reducer;
